/**
 * ════════════════════════════════════════════════════════════
 *  KUTEERR — Global JavaScript  |  style.js
 *  Performance Strategy:
 *   • Deferred execution via DOMContentLoaded
 *   • Passive event listeners everywhere possible
 *   • IntersectionObserver for all scroll-triggered effects
 *   • requestAnimationFrame for scroll/resize handlers
 *   • Conditional init — each module checks for required DOM
 *   • Zero render-blocking — load this file with defer
 * ════════════════════════════════════════════════════════════
 */
(function () {
  'use strict';

  /* ── UTILITIES ──────────────────────────────────────────── */

  // ✅ FIX: optional chaining guards against null ctx
  const $ = (sel, ctx = document) => ctx?.querySelector(sel) ?? null;
  const $$ = (sel, ctx = document) => ctx ? [...ctx.querySelectorAll(sel)] : [];
  const on = (el, ev, fn, opts = {}) => el && el.addEventListener(ev, fn, opts);

  // Throttle via rAF
  function rafThrottle(fn) {
    let ticking = false;
    return function (...args) {
      if (!ticking) {
        requestAnimationFrame(() => { fn.apply(this, args); ticking = false; });
        ticking = true;
      }
    };
  }

  /* ══════════════════════════════════════════════════════════
     1. SCROLL REVEAL (IntersectionObserver)
  ══════════════════════════════════════════════════════════ */
  function initScrollReveal() {
    const targets = $$('.sr, .sr-l, .sr-r');
    if (!targets.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('vis');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -48px 0px' }
    );
    targets.forEach((el) => io.observe(el));
  }

  /* ══════════════════════════════════════════════════════════
     2. STICKY NAV
  ══════════════════════════════════════════════════════════ */
  function initStickyNav() {
    const nav = $('#siteNav');
    if (!nav) return;

    const toggle = rafThrottle(() => {
      nav.classList.toggle('scrolled', window.scrollY > 50);
    });

    on(window, 'scroll', toggle, { passive: true });
  }

  /* ══════════════════════════════════════════════════════════
     3. MOBILE NAV
  ══════════════════════════════════════════════════════════ */
  function initMobileNav() {
    const mob      = $('#mobNav');
    const overlay  = $('#mobOverlay');
    const openBtn  = $('#mobMenuBtn');
    const closeBtn = $('#mobClose');
    const hb1      = $('#hb1');
    const hb2      = $('#hb2');
    const hb3      = $('#hb3');
    if (!mob) return;

    function openNav() {
      mob.classList.add('open');
      document.body.style.overflow = 'hidden';
      if (hb1) hb1.style.transform = 'rotate(45deg) translateY(5px)';
      if (hb2) hb2.style.opacity   = '0';
      if (hb3) hb3.style.transform = 'rotate(-45deg) translateY(-5px)';
    }

    function closeNav() {
      mob.classList.remove('open');
      document.body.style.overflow = '';
      if (hb1) hb1.style.transform = '';
      if (hb2) hb2.style.opacity   = '';
      if (hb3) hb3.style.transform = '';
    }

    on(openBtn,  'click', openNav);
    on(closeBtn, 'click', closeNav);
    on(overlay,  'click', closeNav);
    on(document, 'keydown', (e) => { if (e.key === 'Escape') closeNav(); });

    $$('a, button', mob).forEach((el) => {
      on(el, 'click', () => { if (el.getAttribute('href')) closeNav(); });
    });
  }

  /* ══════════════════════════════════════════════════════════
     4. EMBLA HERO CAROUSEL
  ══════════════════════════════════════════════════════════ */
  // function initHeroSlider() {
  //   const root      = $('#heroSlider');
  //   const container = $('#heroContainer');
  //   if (!root || !container) return;
  //   if (typeof EmblaCarousel === 'undefined') return;

  //   let autoplayPlugin = null;
  //   if (typeof EmblaCarouselAutoplay !== 'undefined') {
  //     try {
  //       autoplayPlugin = EmblaCarouselAutoplay({ delay: 5500, stopOnInteraction: false });
  //     } catch (_) {}
  //   }

  //   const plugins = autoplayPlugin ? [autoplayPlugin] : [];

  //   // duration: 38 = ~600ms transition (Embla uses scroll speed units)
  //   const embla = EmblaCarousel(container, {
  //     loop: true,
  //     duration: 38,
  //     dragFree: false,
  //   }, plugins);

  //   const dots    = $$('.embla-dot');
  //   const counter = $('#counterCurrent');
  //   const prevBtn = $('#heroPrev');
  //   const nextBtn = $('#heroNext');
  //   const slides  = $$('.embla__slide');

  //   function setActive(index) {
  //     // Update dots
  //     dots.forEach((d, i) => d.classList.toggle('active', i === index));

  //     // Update counter
  //     if (counter) counter.textContent = String(index + 1).padStart(2, '0');

  //     // Update is-active on slides (drives Ken-Burns CSS animation)
  //     slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
  //   }

  //   // ✅ FIX: Don't rely on embla 'init' event — it fires BEFORE
  //   //         we attach the listener. Call setActive(0) directly instead.
  //   setActive(embla.selectedScrollSnap()); // always 0 on init

  //   // Update on every slide change
  //   embla.on('select', () => setActive(embla.selectedScrollSnap()));

  //   // Dot clicks
  //   dots.forEach((dot, i) => on(dot, 'click', () => embla.scrollTo(i)));

  //   // Arrow clicks
  //   on(prevBtn, 'click', () => embla.scrollPrev());
  //   on(nextBtn, 'click', () => embla.scrollNext());
  // }

  /* ══════════════════════════════════════════════════════════
     5. FAQ ACCORDION
  ══════════════════════════════════════════════════════════ */
  function initFAQ() {
    const items = $$('.faq-item');
    if (!items.length) return;

    items.forEach((item) => {
      const btn    = $('.faq-question', item);
      const answer = $('.faq-answer', item);
      if (!btn || !answer) return;

      on(btn, 'click', () => {
        const isOpen = item.classList.contains('open');

        items.forEach((it) => {
          it.classList.remove('open');
          const a = $('.faq-answer', it);
          if (a) a.style.maxHeight = '0';
        });

        if (!isOpen) {
          item.classList.add('open');
          answer.style.maxHeight = answer.scrollHeight + 'px';
        }
      });
    });

    // Category filter
    const catBtns = $$('.faq-category-btn');
    catBtns.forEach((btn) => {
      on(btn, 'click', () => {
        catBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const cat = btn.dataset.category;

        items.forEach((item) => {
          item.style.display = (!cat || cat === 'all' || item.dataset.category === cat) ? '' : 'none';
          item.classList.remove('open');
          const a = $('.faq-answer', item);
          if (a) a.style.maxHeight = '0';
        });
      });
    });

    // Live search
    const searchInput = $('#faqSearch');
    if (searchInput) {
      on(searchInput, 'input', rafThrottle(() => {
        const q = searchInput.value.toLowerCase().trim();
        let count = 0;
        items.forEach((item) => {
          const match = !q || item.textContent.toLowerCase().includes(q);
          item.style.display = match ? '' : 'none';
          if (match) count++;
        });
        const countEl = $('#faqResultCount');
        if (countEl) countEl.textContent = q ? `${count} result${count !== 1 ? 's' : ''} found` : '';
      }));
    }
  }

  /* ══════════════════════════════════════════════════════════
     6. CONTACT FORM
  ══════════════════════════════════════════════════════════ */
  function initContactForm() {
    const form = $('#contactForm');
    if (!form) return;

    on(form, 'submit', async (e) => {
      e.preventDefault();
      const submitBtn = $('button[type="submit"]', form);
      const success   = $('#formSuccess');

      if (submitBtn) {
        submitBtn.disabled     = true;
        submitBtn.textContent  = 'Sending…';
      }

      try {
        await new Promise(r => setTimeout(r, 800));
        form.style.display = 'none';
        if (success) success.classList.add('show');
      } catch (_) {
        if (submitBtn) {
          submitBtn.disabled    = false;
          submitBtn.textContent = 'Try Again';
        }
      }
    });
  }

  /* ══════════════════════════════════════════════════════════
     7. NEWSLETTER FORM
  ══════════════════════════════════════════════════════════ */
  function initNewsletter() {
    $$('.newsletter-form').forEach((form) => {
      on(form, 'submit', (e) => {
        e.preventDefault();
        const btn = $('button[type="submit"]', form);
        const inp = $('input[type="email"]', form);
        if (btn) { btn.textContent = 'Subscribed ✦'; btn.disabled = true; }
        if (inp) inp.value = '';
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     8. PRODUCT / COLLECTION FILTER
  ══════════════════════════════════════════════════════════ */
  function initProductFilter() {
    const filterBtns = $$('.filter-btn');
    if (!filterBtns.length) return;

    const grid  = $('#productGrid') || $('#collectionGrid');
    const items = $$('.product-item', grid || document);
    if (!items.length) return;

    filterBtns.forEach((btn) => {
      on(btn, 'click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const filter = btn.dataset.filter;

        items.forEach((item) => {
          const show = !filter || filter === 'all' || item.dataset.category === filter;
          if (show) {
            item.style.display   = '';
            item.style.opacity   = '1';
            item.style.transform = '';
          } else {
            item.style.opacity   = '0';
            item.style.transform = 'scale(.96)';
            setTimeout(() => { item.style.display = 'none'; }, 380);
          }
        });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     9. CRAFT PROCESS TABS
  ══════════════════════════════════════════════════════════ */
  function initCraftTabs() {
    const tabs     = $$('.craft-tab');
    const contents = $$('.craft-content');
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      on(tab, 'click', () => {
        tabs.forEach(t => t.classList.remove('active'));
        tab.classList.add('active');
        const target = tab.dataset.craft;

        contents.forEach((c) => {
          const match = c.dataset.craft === target;
          c.style.display   = match ? '' : 'none';
          c.style.opacity   = match ? '1' : '0';
          c.style.transform = match ? '' : 'translateY(12px)';
        });
      });
    });

    if (tabs[0]) tabs[0].click();
  }

  /* ══════════════════════════════════════════════════════════
     10. ANIMATED NUMBER COUNTERS
  ══════════════════════════════════════════════════════════ */
  function initCounters() {
    const counters = $$('[data-counter]');
    if (!counters.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const el     = e.target;
          const target = parseInt(el.dataset.counter, 10);
          const suffix = el.dataset.suffix || '';
          const inc    = target / (1600 / 16);
          let val      = 0;

          const t = setInterval(() => {
            val += inc;
            if (val >= target) { el.textContent = target + suffix; clearInterval(t); }
            else el.textContent = Math.floor(val) + suffix;
          }, 16);

          io.unobserve(el);
        });
      },
      { threshold: 0.5 }
    );

    counters.forEach((el) => io.observe(el));
  }

  /* ══════════════════════════════════════════════════════════
     11. EVENTS PAGE — FILTER
  ══════════════════════════════════════════════════════════ */
  function initEventsFilter() {
    const filterBtns = $$('.event-filter-btn');
    if (!filterBtns.length) return;

    const events = $$('.event-item');
    filterBtns.forEach((btn) => {
      on(btn, 'click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const filter = btn.dataset.filter;
        events.forEach((ev) => {
          ev.style.display = (!filter || filter === 'all' || ev.dataset.type === filter) ? '' : 'none';
        });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     12. SCROLL-TO-TOP BUTTON
  ══════════════════════════════════════════════════════════ */
  function initScrollTop() {
    const btn = $('#scrollTop');
    if (!btn) return;

    const toggle = rafThrottle(() => {
      const show = window.scrollY > 500;
      btn.style.opacity       = show ? '1' : '0';
      btn.style.pointerEvents = show ? 'auto' : 'none';
    });

    on(window, 'scroll', toggle, { passive: true });
    on(btn, 'click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  }

  /* ══════════════════════════════════════════════════════════
     13. SMOOTH ANCHOR SCROLL (offset for fixed nav)
  ══════════════════════════════════════════════════════════ */
  function initSmoothAnchors() {
    $$('a[href^="#"]').forEach((anchor) => {
      on(anchor, 'click', (e) => {
        const id     = anchor.getAttribute('href');
        const target = id && id !== '#' ? $(id) : null;
        if (!target) return;
        e.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top, behavior: 'smooth' });
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     14. LAZY IMAGES
  ══════════════════════════════════════════════════════════ */
  function initLazyImages() {
    if (!('IntersectionObserver' in window)) return;

    const lazyImgs = $$('img[loading="lazy"]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          const img = e.target;
          if (img.dataset.src) img.src = img.dataset.src;
          io.unobserve(img);
        });
      },
      { rootMargin: '200px 0px' }
    );
    lazyImgs.forEach((img) => io.observe(img));
  }

  /* ══════════════════════════════════════════════════════════
     15. VIDEO MODAL
     ✅ FIX: guard against null modal before any nested $() calls
  ══════════════════════════════════════════════════════════ */
  function initVideoModal() {
    const triggers = $$('[data-video-modal]');
    const modal    = $('#videoModal');

    // ✅ Exit immediately if either element is missing on this page
    if (!triggers.length || !modal) return;

    // Only call $() with modal as ctx AFTER we know modal is not null
    const iframe   = $('iframe', modal);
    const closeBtn = $('#videoModalClose');

    triggers.forEach((btn) => {
      on(btn, 'click', () => {
        const src = btn.dataset.videoModal;
        if (iframe) iframe.src = src + '?autoplay=1';
        modal.style.display = 'flex';
        requestAnimationFrame(() => { modal.style.opacity = '1'; });
        document.body.style.overflow = 'hidden';
      });
    });

    function closeModal() {
      modal.style.opacity = '0';
      setTimeout(() => {
        modal.style.display = 'none';
        if (iframe) iframe.src = '';
        document.body.style.overflow = '';
      }, 300);
    }

    on(closeBtn, 'click', closeModal);
    on(modal,    'click', (e) => { if (e.target === modal) closeModal(); });
    on(document, 'keydown', (e) => { if (e.key === 'Escape') closeModal(); });
  }

  /* ══════════════════════════════════════════════════════════
     16. MARQUEE — respect prefers-reduced-motion
  ══════════════════════════════════════════════════════════ */
  function initMarquee() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      $$('.mq-track').forEach((t) => { t.style.animationPlayState = 'paused'; });
    }
  }

  /* ══════════════════════════════════════════════════════════
     17. KEYBOARD ACCESSIBILITY — Dropdown nav
  ══════════════════════════════════════════════════════════ */
  function initA11yNav() {
    $$('.nav-item').forEach((item) => {
      const btn = $('button.nav-link', item);
      if (!btn) return;

      on(btn, 'keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        item.classList.toggle('open');
        const dropdown = $('.dropdown', item);
        if (dropdown) {
          const isOpen = item.classList.contains('open');
          dropdown.style.opacity    = isOpen ? '1' : '';
          dropdown.style.visibility = isOpen ? 'visible' : '';
        }
      });

      on(item, 'focusout', (e) => {
        if (!item.contains(e.relatedTarget)) item.classList.remove('open');
      });
    });
  }

  /* ══════════════════════════════════════════════════════════
     INIT — DOMContentLoaded
  ══════════════════════════════════════════════════════════ */
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initStickyNav();
    initMobileNav();
    //initHeroSlider();
    initFAQ();
    initContactForm();
    initNewsletter();
    initProductFilter();
    initCraftTabs();
    initCounters();
    initEventsFilter();
    initScrollTop();
    initSmoothAnchors();
    initLazyImages();
    initVideoModal();
    initMarquee();
    initA11yNav();
  });

})();